import { useViewportHeightContext } from "@/contexts/ViewportHeightContext";
import BigMath from "@yn/money/math";
import { useEffect, useMemo, useRef } from "react";

const getVisualViewportMetrics = () => {
    if ( typeof window === "undefined" ) {
        return { height: 0, offsetTop: 0, innerHeight: 0 };
    }

    const vv = window.visualViewport;
    return { height: vv?.height ?? window.innerHeight, offsetTop: vv?.offsetTop ?? 0, innerHeight: window.innerHeight };
};

const hasTextInputFocus = () => {
    if ( typeof document === "undefined" ) return false;
    const active = document.activeElement as HTMLElement | null;
    if ( !active ) return false;

    const tag = active.tagName?.toLowerCase();
    if ( tag === "input" || tag === "textarea" ) return true;
    return Boolean( active.isContentEditable );
};

export function useKeyboardOffset() {
    const { isKeyboardOpen, keyboardHeight } = useIsKeyboardOpen();
    return isKeyboardOpen ? Math.max( 0, keyboardHeight ) : 0;
}
export function useIsKeyboardOpen() {
    const { isKeyboardOpen, keyboardHeight, height } = useViewportHeightContext();
    const baselineHeightRef = useRef<number>( height );

    const inferred = useMemo( () => {
        const isFocused = hasTextInputFocus();

        // 没有焦点时尽量保守：不推断键盘打开
        if ( !isFocused ) {
            return {
                isKeyboardOpen,
                keyboardHeight: isKeyboardOpen ? ( keyboardHeight || 0 ) : 0,
                inferredOpen: false,
            };
        }

        const baseline = baselineHeightRef.current || height || 0;
        const currentHeight = height || 0;

        // system resize(常见 Android adjustResize)：视口变小但无需额外 padding
        const viewportDelta = Number( BigMath.minus( baseline, currentHeight ) ) || 0;
        const relativeThreshold = Number( BigMath.times( baseline, 0.15 ) ) || 0;
        const resizeOpenThreshold = Math.max( 100, relativeThreshold );
        const isSystemResize = viewportDelta > resizeOpenThreshold;

        // overlay：计算覆盖高度，作为 keyboardHeight 的兜底
        const { height: vvHeight, offsetTop, innerHeight } = getVisualViewportMetrics();
        const overlayHeightRaw = Number( BigMath.minus( BigMath.minus( innerHeight, vvHeight ), offsetTop ) ) || 0;
        const overlayHeight = Math.max( 0, overlayHeightRaw );
        const overlayOpenThreshold = 60;
        const isOverlayKeyboard = overlayHeight > overlayOpenThreshold;

        const inferredOpen = isSystemResize || isOverlayKeyboard;
        const finalIsKeyboardOpen = isKeyboardOpen || inferredOpen;

        // 关键：system resize 场景 keyboardHeight 必须为 0，避免二次顶起
        const inferredKeyboardHeight = isOverlayKeyboard ? overlayHeight : 0;
        const finalKeyboardHeight = isKeyboardOpen
            ? ( keyboardHeight || 0 )
            : finalIsKeyboardOpen
            ? inferredKeyboardHeight
            : 0;

        return { isKeyboardOpen: finalIsKeyboardOpen, keyboardHeight: finalKeyboardHeight, inferredOpen };
    }, [ height, isKeyboardOpen, keyboardHeight ] );

    useEffect( () => {
        // 当（推断+原生）都认为键盘关闭时，刷新 baseline，避免旋转/地址栏变化造成长期误判
        if ( !inferred.isKeyboardOpen ) {
            baselineHeightRef.current = Math.max( baselineHeightRef.current || 0, height || 0 );
        }
    }, [ height, inferred.isKeyboardOpen ] );

    return { isKeyboardOpen: inferred.isKeyboardOpen, keyboardHeight: inferred.keyboardHeight };
}
